import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Fingerprint,
  LayoutDashboard,
  Menu,
  MessageCircle,
  ShieldCheck,
  Smartphone,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { useBrand } from "../../context/BrandContext";
import { DEVELOPER_CREDIT } from "../../config/developer";
import {
  MARKETING_WHATSAPP,
  MARKETING_WHATSAPP_DISPLAY,
  buildLeadMessage,
  buildMailtoUrl,
  buildWhatsAppUrl,
} from "../../config/marketing";
import ThemeToggle from "../common/ThemeToggle";
import "./ProductLanding.css";

const FEATURES = [
  {
    icon: Fingerprint,
    title: "حضور وبصمة",
    text: "ربط ساعات ZKTeco، حضور بالموقع، ومتابعة التأخير والانصراف من شاشة واحدة.",
  },
  {
    icon: CalendarDays,
    title: "إجازات وأذونات",
    text: "الموظف يقدم من الموبايل، والمدير يوافق من أي مكان مع دورة موافقات واضحة.",
  },
  {
    icon: Wallet,
    title: "رواتب وسلف",
    text: "مسيرات، قسائم راتب، سلف ومستحقات نقدية بدون شيتات ضائعة.",
  },
  {
    icon: ClipboardCheck,
    title: "موافقات وشفافية",
    text: "كل طلب له مسار: من، متى، ولماذا — للشكاوى والتظلمات والعمل الإضافي.",
  },
  {
    icon: Users,
    title: "هيكل الشركة",
    text: "فروع، إدارات، وظائف، وصلاحيات HR والمدير والمدير العام.",
  },
  {
    icon: Bell,
    title: "إشعارات فورية",
    text: "تنبيه على الموبايل عند كل موافقة أو رفض أو إعلان داخلي.",
  },
  {
    icon: LayoutDashboard,
    title: "تقارير الإدارة",
    text: "حضور يومي، جزاءات، إجراءات، ولوحة للمدير العام بدون انتظار إكسل.",
  },
  {
    icon: Smartphone,
    title: "حساب لكل شركة",
    text: "كود شركة، بيانات معزولة، وشراء مدى الحياة أو اشتراك شهري. التطبيق واحد — الموظف يدخل بحساب شركته.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "نفتح حساب شركتك",
    text: "كود شركة، تجربة 14 يوم، وصلاحيات HR والمدير.",
  },
  {
    num: "02",
    title: "تضيف الموظفين",
    text: "هيكل، ورديات، وبصمة إن وجدت — من غير سيرفر عندك.",
  },
  {
    num: "03",
    title: "الفريق يشتغل",
    text: "الموظف من التطبيق، والإدارة من الويب، بنفس البيانات.",
  },
];

const AUDIENCE = [
  "شركات عايزة تطلع من الورق والإكسل",
  "مؤسسات عندها فروع أو ورديات",
  "شركات شغّالة بساعات بصمة ZKTeco",
  "HR محتاج دورة موافقات واضحة مش شات",
];

function ProductLanding() {
  const brand = useBrand();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [form, setForm] = useState({
    companyName: "",
    contactName: "",
    employeeCount: "21–75",
    phone: "",
    notes: "",
  });
  const [formError, setFormError] = useState("");
  const [formSent, setFormSent] = useState(false);

  const productName = brand.appName || brand.companyName || "Cortex HR";
  const slogan = brand.slogan || "إدارة أذكى لفريقك";
  const whatsappUrl = useMemo(
    () =>
      buildWhatsAppUrl(
        `مرحباً، أريد التعرف على نظام ${productName} لشركتي.`,
      ),
    [productName],
  );

  function closeMenu() {
    setMenuOpen(false);
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleLeadSubmit(event) {
    event.preventDefault();
    setFormError("");
    if (!form.companyName.trim() || !form.contactName.trim()) {
      setFormError("من فضلك أدخل اسم الشركة واسمك.");
      return;
    }

    const message = buildLeadMessage({
      ...form,
      productName,
    });
    const url = buildWhatsAppUrl(message) || buildMailtoUrl(message);

    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      setFormSent(true);
      return;
    }

    setFormError("تعذر إرسال الطلب حالياً. تواصل عبر واتساب المبيعات.");
  }

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <a href="#top" className="lp-brand" onClick={closeMenu}>
            {!logoFailed ? (
              <img
                src={brand.logoPath}
                alt={productName}
                className="lp-brand-logo"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="lp-brand-fallback">{brand.shortName}</span>
            )}
            <span>
              <strong>{productName}</strong>
              <small>{slogan}</small>
            </span>
          </a>

          <nav className="lp-nav-links" aria-label="أقسام الصفحة">
            <a href="#features">المميزات</a>
            <a href="#how">كيف يبدأ</a>
            <a href="#saas">المنصة</a>
            <a href="#contact">تواصل</a>
          </nav>

          <div className="lp-nav-actions">
            <ThemeToggle variant="icon" />
            <Link to="/login" className="lp-btn lp-btn-ghost">
              دخول النظام
            </Link>
            <a href="#contact" className="lp-btn lp-btn-primary lp-btn-nav-cta">
              ابدأ التجربة
            </a>
            <button
              type="button"
              className="lp-menu-btn"
              aria-label={menuOpen ? "إغلاق القائمة" : "فتح القائمة"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {menuOpen ? (
          <div className="lp-mobile-menu">
            <a href="#features" onClick={closeMenu}>
              المميزات
            </a>
            <a href="#how" onClick={closeMenu}>
              كيف يبدأ
            </a>
            <a href="#saas" onClick={closeMenu}>
              المنصة
            </a>
            <a href="#contact" onClick={closeMenu}>
              تواصل
            </a>
            <Link to="/login" onClick={closeMenu}>
              دخول النظام
            </Link>
          </div>
        ) : null}
      </header>

      <main id="top">
        <section className="lp-hero">
          <div className="lp-hero-copy">
            <p className="lp-kicker">منصة موارد بشرية — شراء أو اشتراك</p>
            <h1>
              حضور، طلبات، ورواتب
              <span> من غير ورق ولا إكسل</span>
            </h1>
            <p className="lp-lead">
              {productName} منصة سحابية: كل شركة لها حساب معزول وكود دخول.
              الويب للإدارة، والتطبيق للموظف — شراء مدى الحياة أو اشتراك شهري
              من غير سيرفر عندك.
            </p>
            <div className="lp-hero-cta">
              <a href="#contact" className="lp-btn lp-btn-primary lp-btn-lg">
                تواصل معنا
              </a>
            </div>
            <ul className="lp-hero-points">
              <li>
                <CheckCircle2 size={16} /> حساب شركة معزول على نفس المنصة
              </li>
              <li>
                <CheckCircle2 size={16} /> دخول الموظفين بكود الشركة
              </li>
              <li>
                <CheckCircle2 size={16} /> تجربة 14 يوم ثم شراء مدى الحياة أو اشتراك شهري
              </li>
            </ul>
          </div>

          <div className="lp-hero-visual" aria-hidden="true">
            <div className="lp-phone">
              <div className="lp-phone-notch" />
              <div className="lp-phone-screen">
                <div className="lp-phone-top">
                  <span>{brand.shortName || "HR"}</span>
                  <small>اليوم</small>
                </div>
                <div className="lp-phone-clock">
                  <Clock size={22} />
                  <strong>09:02</strong>
                  <span>تم تسجيل الحضور</span>
                </div>
                <div className="lp-phone-actions">
                  <span>إجازة</span>
                  <span>إذن</span>
                  <span>سلفة</span>
                </div>
                <div className="lp-phone-card">
                  <b>طلب إجازة سنوية</b>
                  <em>قيد موافقة المدير</em>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-strip">
          <div>
            <strong>SaaS</strong>
            <span>اشتراك، مش تركيب سيرفر</span>
          </div>
          <div>
            <strong>كود شركة</strong>
            <span>بيانات كل عميل معزولة</span>
          </div>
          <div>
            <strong>14 يوم تجربة</strong>
            <span>بعدها شراء مدى الحياة أو اشتراك</span>
          </div>
          <div>
            <strong>ويب + موبايل</strong>
            <span>نفس البيانات لحظياً</span>
          </div>
        </section>

        <section id="features" className="lp-section">
          <div className="lp-section-head">
            <h2>كل اللي HR محتاجه في مكان واحد</h2>
            <p>من بصمة الصباح لحد قسيمة الراتب — من غير ملفات متناثرة.</p>
          </div>
          <div className="lp-feature-grid">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className="lp-feature">
                  <span className="lp-feature-icon">
                    <Icon size={22} />
                  </span>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section id="how" className="lp-section lp-section-muted">
          <div className="lp-section-head">
            <h2>التشغيل في ثلاث خطوات</h2>
            <p>من غير سيرفر عند الشركة. نفتح الحساب وتبدأوا.</p>
          </div>
          <div className="lp-steps">
            {STEPS.map((step) => (
              <article key={step.num} className="lp-step">
                <span>{step.num}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="saas" className="lp-section">
          <div className="lp-split">
            <div>
              <p className="lp-kicker">منصة واحدة — حساب لكل شركة</p>
              <h2>SaaS: تشترك، متشتريش سيرفر</h2>
              <p className="lp-lead">
                كل الشركات على نفس المنصة. بيانات شركتك معزولة، والموظف يدخل
                بكود الشركة. الهوية الخاصة (لوجو وتطبيق باسمكم) إضافة لباقة
                المؤسسات.
              </p>
              <ul className="lp-list">
                {AUDIENCE.map((item) => (
                  <li key={item}>
                    <ShieldCheck size={18} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-brand-card">
              <Building2 size={28} />
              <h3>تجربة 14 يوم</h3>
              <p>
                نفتح حساب تجريبي لشركتك. بعد التجربة تختار شراء مدى الحياة
                أو الاشتراك الشهري من غير نقل سيرفرات.
              </p>
              <a href="#contact" className="lp-btn lp-btn-primary">
                تواصل معنا
              </a>
            </div>
          </div>
        </section>

        <section id="contact" className="lp-section">
          <div className="lp-section-head">
            <h2>افتح حساب شركتك</h2>
            <p>نرد عليك، نفتح التجربة، وبعد 14 يوم تختار الشراء أو الاشتراك.</p>
          </div>
          <div className="lp-contact">
            <form className="lp-form" onSubmit={handleLeadSubmit}>
              <label>
                اسم الشركة
                <input
                  value={form.companyName}
                  onChange={(e) => updateField("companyName", e.target.value)}
                  placeholder="شركة النور"
                  autoComplete="organization"
                />
              </label>
              <label>
                اسمك
                <input
                  value={form.contactName}
                  onChange={(e) => updateField("contactName", e.target.value)}
                  placeholder="أحمد"
                  autoComplete="name"
                />
              </label>
              <label>
                عدد الموظفين تقريباً
                <select
                  value={form.employeeCount}
                  onChange={(e) => updateField("employeeCount", e.target.value)}
                >
                  <option>1–20</option>
                  <option>21–75</option>
                  <option>أكثر من 75</option>
                </select>
              </label>
              <label>
                رقم الواتساب
                <input
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  placeholder="01xxxxxxxxx"
                  autoComplete="tel"
                />
              </label>
              <label className="lp-form-full">
                ملاحظات
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  placeholder="عندنا فروع / ساعات بصمة / محتاجين رواتب..."
                />
              </label>
              {formError ? (
                <p className="lp-form-error" role="alert">
                  {formError}
                </p>
              ) : null}
              {formSent ? (
                <p className="lp-form-ok" role="status">
                  تم تجهيز الطلب. أكمل الإرسال من النافذة التي فُتحت.
                </p>
              ) : null}
              <button type="submit" className="lp-btn lp-btn-primary lp-btn-lg">
                إرسال الطلب
              </button>
            </form>

            <aside className="lp-contact-side">
              <h3>أسرع طريقة</h3>
              <p>واتساب مباشرة، ونرد عليك لفتح حساب شركتك.</p>
              {whatsappUrl ? (
                <a
                  className="lp-btn lp-btn-whatsapp"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MessageCircle size={18} />
                  واتساب المبيعات
                </a>
              ) : null}
              <p className="lp-contact-note">
                {MARKETING_WHATSAPP
                  ? `واتساب المبيعات: ${MARKETING_WHATSAPP_DISPLAY}`
                  : "املأ النموذج وسنرد عليك."}
              </p>
            </aside>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <span>{productName}</span>
        <span dir="ltr" lang="en">
          {DEVELOPER_CREDIT}
        </span>
      </footer>
    </div>
  );
}

export default ProductLanding;
